import "../styles/translation-controls.scss";
import { useState, useEffect } from "react";
import actions from "../actions/actions";
import { GuiSelect } from "./global-ui/gui-select";
import { GuiButton } from "./global-ui/gui-button";

// Module-level cache so the language list is fetched only once across all instances
let _cachedLanguages = null;
let _fetchPromise = null;
let _rawLanguageData = null; // Store raw data for filtering

function getLanguageListResult(result) {
  if (Array.isArray(result)) return result;
  return (
    result?.items ||
    result?.data ||
    result?.list ||
    result?.languages ||
    result?.results ||
    []
  );
}

function normalizeLanguageOptions(result, filterByVoiceId = false) {
  const list = getLanguageListResult(result);
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      // If filtering for voice/TTS, only include items with voiceID (including 0)
      if (filterByVoiceId && item?.voiceID == null) {
        return null;
      }

      const value =
        item?.textCode ||
        item?.TextCode ||
        item?.languageCode ||
        item?.code ||
        item?.cultureCode ||
        item?.isoCode ||
        item?.value ||
        item?.id;
      const label =
        item?.name ||
        item?.languageName ||
        item?.displayName ||
        item?.label ||
        value;

      if (!value || !label) return null;
      return { value: value + "", label: label + "" };
    })
    .filter(Boolean);
}

function getTranslatedText(result) {
  if (!result) return "";
  if (typeof result === "string") return result;

  return (
    result?.translatedText ||
    result?.translation ||
    result?.translated ||
    result?.text ||
    result?.data?.translatedText ||
    result?.data?.translation ||
    ""
  );
}

function loadLanguages(filterByVoiceId = false) {
  if (_cachedLanguages && _rawLanguageData) {
    return Promise.resolve(
      normalizeLanguageOptions(_rawLanguageData, filterByVoiceId),
    );
  }
  if (!_fetchPromise) {
    _fetchPromise = actions.language
      .getLanguages()
      .then((result) => {
        _rawLanguageData = result; // Store raw data
        _cachedLanguages = normalizeLanguageOptions(result, false); // Cache all languages
        return normalizeLanguageOptions(result, filterByVoiceId);
      })
      .catch(() => {
        _fetchPromise = null; // allow retry on next mount
        return [];
      });
  }
  return _fetchPromise.then(() =>
    normalizeLanguageOptions(_rawLanguageData, filterByVoiceId),
  );
}

/**
 * TranslationControls
 *
 * Props:
 *   type          – "text" | "email" | "voice"
 *   sourceText    – plain text to translate (caller strips HTML for email)
 *   onTranslated(translatedText) – called with the translated string on success
 *   includeTTS    – (optional) if true, filter languages by voiceID even for non-voice types
 */
export default function TranslationControls({
  type,
  sourceText,
  onTranslated,
  includeTTS = false,
}) {
  const isVoiceType = type === "voice";
  const shouldFilterByVoice = isVoiceType || includeTTS;

  // For voice type or when TTS is included, filter languages to only those with voiceID
  const initialLanguages = _rawLanguageData
    ? normalizeLanguageOptions(_rawLanguageData, shouldFilterByVoice)
    : [];

  const [languages, setLanguages] = useState(initialLanguages || []);
  const [selectedLanguage, setSelectedLanguage] = useState(
    initialLanguages?.[0]?.value || "",
  );
  const [loadingLanguages, setLoadingLanguages] = useState(!_rawLanguageData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!_rawLanguageData) {
      // Data not loaded yet, load it
      loadLanguages(shouldFilterByVoice).then((options) => {
        setLanguages(options);
        if (options.length > 0) setSelectedLanguage(options[0].value);
        setLoadingLanguages(false);
      });
    } else {
      // Data already loaded, re-filter based on current shouldFilterByVoice
      const filteredOptions = normalizeLanguageOptions(
        _rawLanguageData,
        shouldFilterByVoice,
      );
      setLanguages(filteredOptions);
      if (filteredOptions.length > 0)
        setSelectedLanguage(filteredOptions[0].value);
      setLoadingLanguages(false);
    }
  }, [shouldFilterByVoice]);

  const hasContent = (sourceText || "").trim().length > 0;
  const translationSelectStyle = {
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menu: (base) => ({ ...base, zIndex: 10000 }),
  };

  async function handleTranslate() {
    if (!hasContent || !selectedLanguage) return;
    setError("");
    setLoading(true);
    try {
      const result = await actions.language.translateText(
        (sourceText || "").trim(),
        selectedLanguage,
      );
      const translatedText = getTranslatedText(result);
      if (result?.success === false || !translatedText) {
        setError(
          result?.errorMessage ||
            "Translation could not be completed. Please try again.",
        );
        return;
      }
      onTranslated(translatedText, selectedLanguage);
    } catch {
      setError("Translation could not be completed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="translation-block">
        <div className="translation-label">Translate</div>
        <div className="translation-note">
          <i className="fas fa-info-circle translation-note-icon" />
          <span>Only English text can be translated to other languages.</span>
        </div>
        <div className="translation-controls">
          <div className="translation-language-select">
            <GuiSelect
              name="translationLanguage"
              value={selectedLanguage}
              options={languages}
              inModal={true}
              style={translationSelectStyle}
              disabled={loadingLanguages || loading}
              onChange={(_name, item) => {
                setSelectedLanguage(item?.value || "");
                setError("");
              }}
              placeholder="Select language"
            />
          </div>
          <GuiButton
            className="btn-outline translate-btn"
            dataQa={`translate-${type}`}
            disabled={!hasContent || !selectedLanguage || loadingLanguages}
            loading={loading}
            onClick={handleTranslate}
          >
            Translate
          </GuiButton>
        </div>
      </div>
      {error && <div className="error-msg translation-error">{error}</div>}
    </>
  );
}
